import type { Field, PageVariableSchema, TypeInstanceMap } from '@lighthouse/core'
import { getPageParameterAndVariableOptions, getUserDatasourceOptions, useAtomData } from '@lighthouse/shared'
import { useCallback, useMemo } from 'react'

import { dataSourceListAtomFamily, userDataSourceAtom } from '@/atoms/dataSource/state'
import {
    languagePageListAtom,
    pageAtomFamily,
    pageListAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'

import { useCurrentAppId, useCurrentEnvId } from './useApplication'

export const usePageList = () => {
    return useAtomData(pageListAtom)
}

export const useCurrentLanguagePageList = () => {
    return useAtomData(languagePageListAtom)
}

/**
 * 获取页面参数及变量的options
 *
 * @param {({ pageId: string })} param0
 * @param {string} param0.pageId
 * @returns {*}
 */
export const useGetPageParameterAndVariableOptions = ({
    pageId,
    disabledPageState,
    pageVariableFilter,
    includeDeviceId = true
}: {
    pageId: string
    disabledPageState?: boolean
    pageVariableFilter?: (variable: PageVariableSchema, index: number) => boolean
    includeDeviceId?: boolean
}) => {
    const pageVariables = useAtomData(
        pageVariableSchemasFamily(pageId),
        useCallback(schemas => (pageVariableFilter ? schemas?.filter(pageVariableFilter) : schemas), [pageVariableFilter])
    )
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useAtomData(dataSourceListAtomFamily({ appId, envId }))

    return useMemo(
        () =>
            getPageParameterAndVariableOptions({
                pagePathParameter,
                pageQueryParameter,
                pageVariables,
                pageStateParameter: disabledPageState ? undefined : pageStateParameter,
                dataSourceList,
                includeDeviceId
            }),
        [pagePathParameter, pageQueryParameter, pageVariables, disabledPageState, pageStateParameter, dataSourceList, includeDeviceId]
    )
}

/**
 * 获取用户数据变量的option
 *
 * @param {GetOptionsParams} param0
 * @param {(field: Field) => boolean} param0.fieldFilter
 * @returns {*}
 */
export const useGetUserRecordOptions = () => {
    const userDataSource = useAtomData(userDataSourceAtom)

    return useMemo(
        () =>
            getUserDatasourceOptions({
                dataSource: userDataSource
            }),
        [userDataSource]
    )
}
